import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, Landmark, TrendingUp, Building2, Search } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { PoliticosPanel, ServidoresPanel } from "../components/Panels";
import Header from "../components/Header";
import { api, brl, dateBR } from "../lib/api";
import { HOME } from "../constants/testIds";

const POLLING_INTERVAL_MS = 3500;

function StatusMessage({ status }) {
  if (status?.running) {
    return (
      <span className="text-emerald-800">
        ● Ingestão em andamento — estágio: <b>{status.stage}</b>
      </span>
    );
  }
  if (status?.last_success_at) {
    return <span>Última atualização: {dateBR(status.last_success_at)}</span>;
  }
  return <span>Aguardando primeira ingestão</span>;
}

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="stat-tile" data-testid={HOME.overviewCard}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-slate-400">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">
            {value}
          </div>
          {sub && (
            <div className="mt-1 text-xs text-slate-500">{sub}</div>
          )}
        </div>
        {Icon && (
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-sm ${accent}`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function HomePage() {
  const [status, setStatus] = useState({});
  const [stats, setStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("servidores");
  const [heroSearch, setHeroSearch] = useState("");
  const [politicoSearch, setPoliticoSearch] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([
        api.get("/ingestion/status"),
        api.get("/stats/overview"),
      ]);
      setStatus(s.data || {});
      setStats(o.data || null);
      return s.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Poll while ingestion is running
  useEffect(() => {
    if (!status?.running) return;
    setRefreshing(true);
    const t = setInterval(async () => {
      const s = await loadStatus();
      if (s && !s.running) {
        clearInterval(t);
        setRefreshing(false);
        setRefreshKey((k) => k + 1);
        if (s.stage === "done") {
          toast.success("Dados atualizados com sucesso.");
        } else if (s.stage === "error") {
          toast.error("Falha ao atualizar os dados. Tente novamente mais tarde.");
        }
      }
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(t);
  }, [status?.running, loadStatus]);

  const heroYear = useMemo(() => stats?.ano || "—", [stats?.ano]);

  return (
    <div className="min-h-screen">
      <Header refreshing={refreshing} status={status} />

      {/* HERO */}
      <section className="hero-noise border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 pb-10 pt-12 md:px-10 md:pb-16 md:pt-16">
          <div className="grid gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <div className="overline mb-4 flex items-center gap-3 text-emerald-800">
                <span className="h-px w-8 bg-emerald-800/40" />
                Fiscalização Cidadã · Ano-base {heroYear}
              </div>
              <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                Quem <span className="italic text-emerald-800">mais custa</span>
                <br />
                aos cofres públicos do Brasil?
              </h1>
              <p className="mt-6 max-w-xl text-base text-slate-600">
                Ranking anual dos 100 servidores federais com maior remuneração
                total e uma aba dedicada aos parlamentares do Congresso — dados
                oficiais do Portal da Transparência, Câmara e Senado.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                <span className="gov-badge">Portal da Transparência</span>
                <span className="gov-badge">Câmara dos Deputados</span>
                <span className="gov-badge">Senado Federal</span>
              </div>

              {/* Hero politician search */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const q = heroSearch.trim();
                  if (!q) return;
                  setPoliticoSearch(q);
                  setTab("politicos");
                  document
                    .getElementById("ranking-section")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="mt-8 flex flex-col gap-2 sm:max-w-xl"
                data-testid="hero-search-form"
              >
                <label
                  htmlFor="hero-search"
                  className="overline text-emerald-800"
                >
                  Pesquise aqui seu Político de Estimação
                </label>
                <div className="relative flex items-stretch overflow-hidden rounded-md border border-slate-300 bg-white transition-colors focus-within:border-emerald-800">
                  <div className="pointer-events-none flex items-center pl-4 text-emerald-800">
                    <Search className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <input
                    id="hero-search"
                    data-testid="hero-search-input"
                    type="text"
                    placeholder="Digite o nome (ex.: Lula, Cabo Daciolo, Bolsonaro…)"
                    value={heroSearch}
                    onChange={(e) => setHeroSearch(e.target.value)}
                    className="h-12 w-full flex-1 bg-transparent px-3 text-base text-slate-900 outline-none placeholder:text-slate-400"
                  />
                  <button
                    type="submit"
                    data-testid="hero-search-submit"
                    className="flex items-center gap-1.5 bg-emerald-900 px-5 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-emerald-800"
                  >
                    Buscar
                  </button>
                </div>
              </form>
            </div>

            <div className="grid grid-cols-2 gap-4 self-end">
              <Stat
                label="Servidores no ranking"
                value={stats?.counts?.servidores ?? "—"}
                sub="Top 100 · Executivo Federal"
                icon={Users}
                accent="bg-emerald-50 text-emerald-800"
              />
              <Stat
                label="Deputados federais"
                value={stats?.counts?.deputados ?? "—"}
                sub="Câmara · CEAP + salários"
                icon={Landmark}
                accent="bg-amber-50 text-amber-700"
              />
              <Stat
                label="Senadores"
                value={stats?.counts?.senadores ?? "—"}
                sub="Senado · Verba indenizatória"
                icon={Building2}
                accent="bg-sky-50 text-sky-700"
              />
              <Stat
                label="Gasto agregado"
                value={
                  stats
                    ? brl(
                        (stats.totals?.servidores || 0) +
                          (stats.totals?.politicos || 0),
                      )
                    : "—"
                }
                sub={
                  status?.last_success_at
                    ? `Atualizado em ${dateBR(status.last_success_at)}`
                    : "Sem snapshot ainda"
                }
                icon={TrendingUp}
                accent="bg-slate-100 text-slate-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section
        id="ranking-section"
        className="mx-auto max-w-[1400px] px-6 py-10 md:px-10 md:py-14"
      >
        <Tabs value={tab} onValueChange={setTab} data-testid={HOME.tabsRoot}>
          <div className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-1 sm:flex-row sm:items-end sm:justify-between">
            <TabsList className="h-auto bg-transparent p-0">
              <TabsTrigger
                value="servidores"
                data-testid={HOME.tabServidores}
                className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-semibold uppercase tracking-wider text-slate-500 data-[state=active]:border-emerald-800 data-[state=active]:bg-transparent data-[state=active]:text-emerald-900 data-[state=active]:shadow-none"
              >
                Servidores
              </TabsTrigger>
              <TabsTrigger
                value="politicos"
                data-testid={HOME.tabPoliticos}
                className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-semibold uppercase tracking-wider text-slate-500 data-[state=active]:border-emerald-800 data-[state=active]:bg-transparent data-[state=active]:text-emerald-900 data-[state=active]:shadow-none"
              >
                Políticos
              </TabsTrigger>
            </TabsList>

            <div className="text-xs text-slate-500">
              <StatusMessage status={status} />
            </div>
          </div>

          <TabsContent value="servidores" className="mt-0">
            <ServidoresPanel refreshKey={refreshKey} />
          </TabsContent>
          <TabsContent value="politicos" className="mt-0">
            <PoliticosPanel refreshKey={refreshKey} initialSearch={politicoSearch} />
          </TabsContent>
        </Tabs>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 py-8 text-xs text-slate-500 md:px-10">
          Dados oficiais consultados via APIs públicas do Governo Federal.
          Este projeto é independente e não possui vínculo com qualquer órgão
          governamental.
        </div>
      </footer>
    </div>
  );
}
